import { Fragment } from 'react';
import PropTypes from 'prop-types';
import NoSSR from 'react-no-ssr';
import { trans } from '@spotahome/soyuz/client';

import { getDateAsUTC } from '../utils/dateUtils';
import { Tooltip, InfoIcon } from '../..';

import PrivateLandlord from '../icons/PrivateLandlord';
import ProfessionalLandlord from '../icons/ProfessionalLandlord';
import Verified from '../icons/Verified';

import {
  LANDLORD_TYPES,
  PRIVATE_LANDLORD,
  PROFESSIONAL_LANDLORD
} from './constants';

import './ExtendedLandlordInformationContent.scss';

const LandlordInformationPiece = ({ title, subtitle }) => (
  <div className="extended-landlord-information-piece">
    <span className="extended-landlord-information-piece__title">{title}</span>
    <span className="extended-landlord-information-piece__subtitle">
      {subtitle}
    </span>
  </div>
);

LandlordInformationPiece.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string.isRequired
};

const NEVER_CANCELS = 'never';
const RARELY_CANCELS = 'rarely';
const EXCEPTIONAL_INTEREST = 'exceptional';
const GOOD_INTEREST = 'good';

const ExtendedLandlordInformationContent = ({
  type,
  joiningDate,
  listingsPublished,
  averageResponseTime,
  numberOfBookingRequests = null,
  languages = [],
  renderOtherProperties = () => {},
  firstName = null,
  cancellationRate = null,
  interestRate = null
}) => {
  const MAIN_LANGUAGES = [
    'spanish',
    'english',
    'portuguese',
    'french',
    'german',
    'italian'
  ];

  const filteredCommonLanguages = languages.filter(language =>
    MAIN_LANGUAGES.includes(language)
  );

  const spokenLanguagesTranslated = filteredCommonLanguages
    .map(language => trans(`landlord_language_${language}`))
    .join(', ');

  const LANDLORD_ICONS = {
    [PRIVATE_LANDLORD]: () => <PrivateLandlord />,
    [PROFESSIONAL_LANDLORD]: () => <ProfessionalLandlord />
  };

  return (
    <Fragment>
      <div className="extended-landlord-information-content__header">
        <div className="extended-landlord-information-content__icon">
          {LANDLORD_ICONS[type]()}
        </div>
        <div className="extended-landlord-information-content__title">
          <div className="extended-landlord-information-content__type">
            {firstName
              ? `${firstName} - ${trans(`listing.landlord_info.type.${type}`)}`
              : trans(`listing.landlord_info.type.${type}`)}
          </div>
          <div className="extended-landlord-information-content__verified">
            <div>
              <Verified />
            </div>
            <div>{trans('listing.landlord_info.verified')}</div>
            <NoSSR>
              <Tooltip
                color="blue-cerulean"
                tooltip={trans(`listing.landlord_info.type.${type}.tooltip`)}
                trigger={['click', 'hover']}
              >
                <InfoIcon size={12} />
              </Tooltip>
            </NoSSR>
          </div>
          {(cancellationRate === NEVER_CANCELS ||
            cancellationRate === RARELY_CANCELS) && (
            <div className="extended-landlord-information-content__landlord-info">
              {trans(`listing.landlord_info.cancel.info.${cancellationRate}`)}
            </div>
          )}
          {(interestRate === EXCEPTIONAL_INTEREST ||
            interestRate === GOOD_INTEREST) && (
            <div className="extended-landlord-information-content__landlord-info">
              {trans(`listing.landlord_info.interest.info.${interestRate}`)}
            </div>
          )}
          <div className="extended-landlord-information-content__landlord-info">
            {trans('listing.landlord_info.languages', {
              langs:
                spokenLanguagesTranslated || trans('landlord_language_english')
            })}
          </div>
        </div>
      </div>
      <div className="extended-landlord-information-content__list">
        <Fragment>
          <LandlordInformationPiece
            title={listingsPublished}
            subtitle={trans('listing.landlord_info.published.subtitle')}
          />
        </Fragment>

        {joiningDate && (
          <LandlordInformationPiece
            title={trans('listing.landlord_info.with_us.title', {
              year: getDateAsUTC(new Date(joiningDate)).getFullYear()
            })}
            subtitle={trans('listing.landlord_info.with_us.subtitle')}
          />
        )}
        {numberOfBookingRequests > 0 ? (
          <>
            <LandlordInformationPiece
              title={numberOfBookingRequests}
              subtitle={trans(
                'listing.landlord_info.booking_requests.subtitle'
              )}
            />
            <LandlordInformationPiece
              title={averageResponseTime}
              subtitle={trans(
                'listing.landlord_info.average_response_time.subtitle'
              )}
            />
          </>
        ) : null}
      </div>
      {renderOtherProperties()}
    </Fragment>
  );
};

ExtendedLandlordInformationContent.propTypes = {
  type: PropTypes.oneOf(LANDLORD_TYPES).isRequired,
  joiningDate: PropTypes.string.isRequired,
  listingsPublished: PropTypes.number.isRequired,
  averageResponseTime: PropTypes.string.isRequired,
  numberOfBookingRequests: PropTypes.number,
  languages: PropTypes.arrayOf(PropTypes.string),
  renderOtherProperties: PropTypes.func,
  firstName: PropTypes.string,
  cancellationRate: PropTypes.bool,
  interestRate: PropTypes.bool
};

export default ExtendedLandlordInformationContent;
